import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PageComponent } from '../../../../../services/website.service';
import { WebsiteService } from '../../../../../services/website.service';
import { ProductService, Product } from '../../../../../services/product.service';
import { Subject } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';

@Component({
  selector: 'app-products-component',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './products-component.component.html',
  styleUrls: ['./products-component.component.scss']
})
export class ProductsComponentComponent implements OnInit, OnDestroy, OnChanges {
  @Input() component!: PageComponent;
  @Input() mode: 'preview' | 'edit' = 'preview';
  @Input() isSelected: boolean = false;
  @Input() currentLocale: string = 'nl';
  @Output() styleChange = new EventEmitter<void>();
  @Output() removeProduct = new EventEmitter<string>();

  private destroy$ = new Subject<void>();
  products: Product[] = [];
  filteredProducts: Product[] = [];
  loading = false;

  constructor(
    private productService: ProductService,
    private websiteService: WebsiteService
  ) {}

  ngOnInit() {
    // Only load products if there are selected product IDs
    const selectedProductIds = this.getSelectedProductIds();
    if (selectedProductIds && selectedProductIds.length > 0) {
      this.loadProducts();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Reload products when selectedProductIds changes
    if (changes['component']) {
      const currentSelectedIds = this.getSelectedProductIds();
      const previousSelectedIds = changes['component'].previousValue?.data?.['selectedProductIds'] || [];
      
      // Check if selectedProductIds actually changed
      const currentIdsStr = JSON.stringify(currentSelectedIds || []);
      const previousIdsStr = JSON.stringify(previousSelectedIds);
      
      if (currentIdsStr !== previousIdsStr) {
        // Selection changed, reload products
        this.loadProducts();
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadProducts() {
    const selectedProductIds = this.getSelectedProductIds();
    
    // Don't load if no products are selected
    if (!selectedProductIds || selectedProductIds.length === 0) {
      this.products = [];
      this.filteredProducts = [];
      this.loading = false;
      return;
    }

    this.loading = true;
    this.productService.getProducts()
      .pipe(
        takeUntil(this.destroy$),
        map(products => {
          // Filter products by current domain
          const currentDomain = this.websiteService.getCurrentDomain();
          if (currentDomain) {
            return products.filter(p => {
              const domains = p['domains'] || [];
              return domains.includes(currentDomain);
            });
          }
          return products;
        })
      )
      .subscribe({
        next: (products) => {
          this.products = products;
          this.filteredProducts = this.applyFilters(products);
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading products:', error);
          this.loading = false;
        }
      });
  }

  hasSelectedProducts(): boolean {
    const selectedProductIds = this.getSelectedProductIds();
    return selectedProductIds && selectedProductIds.length > 0;
  }

  applyFilters(products: Product[]): Product[] {
    let filtered = [...products];
    
    // Always filter by selected product IDs (this method is only called when products are selected)
    const selectedProductIds = this.getSelectedProductIds();
    if (selectedProductIds && selectedProductIds.length > 0) {
      filtered = filtered.filter(p => selectedProductIds.includes(p.id || ''));
    }

    return filtered;
  }

  // Getters
  getViewType(): string {
    return this.component.data['viewType'] || 'grid';
  }

  getColumns(): number {
    return this.component.data['columns'] || 3;
  }

  getCategoryFilter(): string {
    return this.component.data['categoryFilter'] || '';
  }

  getLimit(): number {
    return this.component.data['limit'] || 0;
  }

  getSelectedProductIds(): string[] {
    return this.component.data['selectedProductIds'] || [];
  }

  getShowPrice(): boolean {
    return this.component.data['showPrice'] !== false;
  }

  getShowDescription(): boolean {
    return this.component.data['showDescription'] !== false;
  }

  getHeading(): string {
    return this.component.data['heading'] || '';
  }

  // Styling getters
  getBackgroundType(): string {
    return this.component.data['backgroundType'] || 'solid';
  }

  getBackgroundColor(): string {
    return this.component.data['backgroundColor'] || '#ffffff';
  }

  getGradientFrom(): string {
    return this.component.data['gradientFrom'] || '#ffffff';
  }

  getGradientTo(): string {
    return this.component.data['gradientTo'] || '#ffffff';
  }

  getBackgroundImage(): string {
    return this.component.data['backgroundImage'] || '';
  }

  getTitlePosition(): string {
    return this.component.data['titlePosition'] || 'left';
  }

  getHeadingColor(): string {
    return this.component.data['headingColor'] || '#111827';
  }

  getTextColor(): string {
    return this.component.data['textColor'] || '#374151';
  }

  getParallax(): boolean {
    return this.component.data['parallax'] || false;
  }

  getSectionHeight(): string {
    return this.component.data['sectionHeight'] || '';
  }

  getHeightUnit(): string {
    return this.component.data['heightUnit'] || 'px';
  }

  getPreviewStyles(): { [key: string]: string } {
    const styles: { [key: string]: string } = {};
    const backgroundType = this.getBackgroundType();

    if (backgroundType === 'solid') {
      styles['background-color'] = this.getBackgroundColor();
    } else if (backgroundType === 'gradient') {
      styles['background'] = `linear-gradient(to bottom, ${this.getGradientFrom()}, ${this.getGradientTo()})`;
    } else if (backgroundType === 'image') {
      const bgImage = this.getBackgroundImage();
      if (bgImage) {
        styles['background-image'] = `url(${bgImage})`;
        styles['background-size'] = 'cover';
        styles['background-position'] = 'center';
        if (this.getParallax()) {
          styles['background-attachment'] = 'fixed';
        }
      }
    }

    const sectionHeight = this.getSectionHeight();
    if (sectionHeight) {
      const unit = this.getHeightUnit();
      styles['min-height'] = `${sectionHeight}${unit}`;
    }

    return styles;
  }

  getTitlePositionClass(): string {
    const position = this.getTitlePosition();
    switch (position) {
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-right';
      default:
        return 'text-left';
    }
  }

  getProductName(product: Product): string {
    const acf = product.acf || {};
    const locale = this.currentLocale;
    return acf[`naam_${locale}`] || acf.naam_nl || product.sku || 'Unnamed Product';
  }

  getProductDescription(product: Product): string {
    const acf = product.acf || {};
    const locale = this.currentLocale;
    const desc = acf[`omschrijving_${locale}`] || acf['omschrijving_nl'] || '';
    // Strip HTML tags for preview
    return desc.replace(/<[^>]*>/g, '').substring(0, 150) + (desc.length > 150 ? '...' : '');
  }

  getProductImage(product: Product): string {
    const images = product['images'];
    if (images && Array.isArray(images) && images.length > 0) {
      return images[0].src || '';
    }
    return '';
  }

  getProductPrice(product: Product): number {
    return product.price || product['regular_price'] || 0;
  }

  formatPrice(price: number): string {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  }

  getGridColumnsClass(): string {
    const cols = this.getColumns();
    const gridClasses: { [key: number]: string } = {
      1: 'grid-cols-1',
      2: 'grid-cols-1 md:grid-cols-2',
      3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
      4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
    };
    return gridClasses[cols] || gridClasses[3];
  }

  onRemoveProduct(productId: string, event: Event) {
    event.stopPropagation();
    this.removeProduct.emit(productId);
  }
}
